import { Alert, Button } from "@mantine/core";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import AppointmentsApi from "../../data/api/appointmentsApi";
import { GenericTransport } from "../../data/api/baseApi";
import ShiftSessionsApi from "../../data/api/shiftSessionsApi";
import { IState } from "../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
  IGameAppointmentDetail,
} from "../../types/appointments/Appointment";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { AppointmentType } from "../../types/appointments/AppointmentType";
import { IGameConfiguration } from "../../types/game/GameConfiguration";
import { BonusType } from "../../types/payrates/bonusType";
import { IPerson } from "../../types/persons/person";
import { IVenue } from "../../types/venue/Venue";
import { toDateTimeNullable } from "../../util/date+helper";
import { ShiftSessionLink } from "../common/Links";
import { SSTable, TableData } from "../common/TableData";
import TextSeparator from "../common/TextSeparator";
import { CenteredLoader } from "../common/loader";
import { ITabBaseState, ITabProps, TabBase } from "../common/tabs/TabBase";
import AddLoadingView from "../management/roster/AddLoadingView";

export interface IAppointmentViewProps {
  appointmentId: string;
  sessionId?: string | null;
  defaultTab?: string | null;
  gameConfigurations?: { [id: string]: IGameConfiguration };

  disableEdits: boolean
  venues: { [id: string]: IVenue };
}

export interface IAppointmentViewState extends ITabBaseState {
  loading?: boolean;
  error?: string;
  appointment?: IAppointment<IAppointmentDetail>;
  persons?: { [id: string]: IPerson };
  data?: GenericTransport<any>;
}

export interface IAppointmentViewTabProps extends ITabProps {
  appointment: IAppointment<IAppointmentDetail>;
  persons: { [id: string]: IPerson };
  gameConfigurations?: { [id: string]: IGameConfiguration };
  data: GenericTransport<any>;
  venues: { [id: string]: IVenue };
  toggle?: () => void;
  reload: () => void;

  disableEdits: boolean;
}

const AppointmentOverviewTab: React.FunctionComponent<
  IAppointmentViewTabProps
> = ({ appointment, disableEdits, venues, ...props }) => {

  const [shiftId, setShiftId] = React.useState<string | null>(null);
  const [canEditShift, setCanEditShift] = React.useState<boolean>(false);

  React.useEffect(() => {

    let cancelled = false;

    if (!appointment.shiftSession) {
      setShiftId(null);
      setCanEditShift(false);
      return;
    }

    Promise.all([
      ShiftSessionsApi.getById(appointment.shiftSession),
      ShiftSessionsApi.hasElevatedShiftPrivileges(appointment.shiftSession, appointment.association),
    ])
      .then(([session, canEdit]) => {
        if (cancelled) return;
        setShiftId(session.shift);
        setCanEditShift(Boolean(canEdit));
      })
      .catch((error) => {
        if (!cancelled) {
          setShiftId(null);
          setCanEditShift(false);
        }
        console.error("Error fetching shift session:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [appointment.shiftSession]);

  const paymentData: Array<[string, React.ReactNode]> = [
    ["Date", toDateTimeNullable(appointment.start)?.toLocaleString(DateTime.DATETIME_MED) || ""]
  ];

  if (appointment.dateMarkedCompleted) {
    paymentData.push([
      "Confirmed",
      toDateTimeNullable(appointment.dateMarkedCompleted)?.toFormat(
        "dd/MM/yyyy hh:mm a",
      ) || "",
    ]);
  }

  paymentData.push(
    ["Base", `$${Number(appointment.payment?.base).toFixed(2)}`],
    [
      "Bonuses",
      appointment.payment?.bonuses
        ? `$${Number(
          appointment.payment?.bonuses?.reduce(
            (prev, val) => prev + val.value || 0,
            0,
          ),
        ).toFixed(2)}`
        : "-",
    ],
    ["Total", `$${Number(appointment.payment?.total).toFixed(2)}`],
  );

  if (appointment.type === AppointmentType.game) {
    const gameDetails = appointment.details as IGameAppointmentDetail;
    let game = props.data?.games?.[0];
    // fallback in case it's wrapped in an extra array
    if (game && Array.isArray(game)) game = game[0];
    const gameConfiguration =
      props.gameConfigurations?.[game?.referees?.config || ""];
    const position =
      gameConfiguration?.fields?.filter(
        (f) => f.identifier == gameDetails.position,
      )?.[0]?.friendlyName || gameDetails.position;
    paymentData.push(
      ["Apprentice", gameDetails.apprentice ? "True" : "False"],
      ["Position", position],
      ["Court", gameDetails?.court || ""],
    );
  }


  const venue = venues[appointment.details?.venue]?.name || "";
  if (venue) {
    paymentData.push(["Venue", venue]);
  }

  if (shiftId && canEditShift) {
    paymentData.push(["Shift", <ShiftSessionLink sessionId={appointment.shiftSession} shiftId={shiftId}>Go to session</ShiftSessionLink>]);
  }

  return (
    <div>
      <TableData data={paymentData} />
    </div>
  );
};

const BonusesTab: React.FunctionComponent<IAppointmentViewTabProps> = ({
  appointment,
  reload,
  disableEdits,
}) => (
  <div style={{ marginTop: "20px" }}>
    {(appointment.payment?.bonuses?.length ?? 0) > 0 ? (
      <>
        <h4>Bonuses</h4>
        <SSTable>
          {appointment.payment?.bonuses?.map((bonus, idx) => (
            <tr>
              <td>{BonusType.toLongReason(bonus.reason)}</td>
              <td>${Number(bonus.value).toFixed(2)}</td>
              <td>{bonus.comment || ""}</td>
            </tr>
          ))}
        </SSTable>
      </>
    ) : (
      <p>
        <em>No bonuses</em>
      </p>
    )}
    {!disableEdits && <>
      <TextSeparator>Add loading</TextSeparator>
      <AddLoadingView appointmentId={appointment._id} onSave={reload} />
    </>}
  </div>
);

const AppointmentStatusHistoryTab: React.FunctionComponent<
  IAppointmentViewTabProps
> = ({ appointment, persons }) => (
  <SSTable>
    <thead>
      <tr>
        <th>Status</th>
        <th>Date</th>
        <th>Person</th>
        <th>Comment</th>
      </tr>
    </thead>
    <tbody>
      {appointment.statuses.map((st, idx) => (
        <tr key={idx}>
          <td>{AppointmentStatus.toString(st.status)}</td>
          <td>{toDateTimeNullable(st.date)?.toFormat("dd/MM/yyyy hh:mm a")}</td>
          <td>{st.person ? persons?.[st.person]?.fullName : ""}</td>
          <td>{st.comment}</td>
        </tr>
      ))}
    </tbody>
  </SSTable>
);

class AppointmentView extends TabBase<
  IAppointmentViewProps,
  IAppointmentViewState,
  IAppointmentViewTabProps
> {
  constructor(props: IAppointmentViewProps, content) {
    super(props, content);

    this.state = {
      tab: props.defaultTab || "Overview",
    };
  }

  componentDidMount() {
    if (this.props.appointmentId) {
      this.loadAppointment(this.props.appointmentId);
    }
  }

  loadAppointment(appointmentId: string) {
    this.setState({ loading: true, error: undefined });
    AppointmentsApi.getAppointmentById(appointmentId, this.props.sessionId)
      .then((resp) => {
        this.setState({
          loading: false,
          appointment: resp.data,
          data: resp,
          persons: _.keyBy(resp.persons, "_id"),
        });
      })
      .catch((er) => {
        this.setState({
          loading: false,
          error: er.message,
        });
      });
  }

  getTabs() {
    const tabs = {
      Overview: AppointmentOverviewTab,
      Bonuses: BonusesTab,
      History: AppointmentStatusHistoryTab,
    };

    return tabs;
  }

  public render() {
    if (this.state.error) {
      return (
        <div style={{ margin: "0 auto", padding: "20px" }}>
          <Alert color="red">{this.state.error}</Alert>
          <Button
            onClick={() => this.loadAppointment(this.props.appointmentId)}
            style={{ margin: "0 auto" }}
          >
            Reload
          </Button>
        </div>
      );
    }

    const { appointment } = this.state;
    if (!appointment) {
      return (
        <div style={{ width: "100%" }}>
          <CenteredLoader />
        </div>
      );
    }

    return (
      <div>
        {this.getTabView({
          appointment,
          data: this.state.data as GenericTransport<any>,
          persons: this.state.persons || {},
          gameConfigurations: this.props.gameConfigurations,
          reload: () => this.loadAppointment(this.props.appointmentId),
          disableEdits: this.props.disableEdits,
          venues: this.props.venues,
        })}
      </div>
    );
  }
}

const mapState2Props = (state: IState, props: IAppointmentViewProps) => {
  const appointment =
    props.appointmentId && state.appointments.map[props.appointmentId];
  return {
    appointment,
    gameConfigurations: state.gameConfigurations.map,
    venues: state.venues.map,
  };
};

export default connect(mapState2Props)(AppointmentView);
